"use client";

import styles from "@/styles/global/category-popup.module.scss";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { Popup, Skeleton } from "react-vant";
import { Arrow, Cross } from "@react-vant/icons";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { closeCategoryPopup } from "@/store/slices/modalSlice";
import systemApi from "@/service/modules/systemApi";

interface CategoryItem {
  categoryID: number;
  name: string;
  url: string;
  icon: string;
  showSubCategory: boolean;
  subCategories: SubCategoryItem[];
}

interface SubCategoryItem {
  categoryID: number;
  name: string;
  url: string;
  showSubCategory: boolean;
  subCategories: GrandChildCategoryItem[];
}

interface GrandChildCategoryItem {
  name: string;
  url: string;
}

export default function CategoryPopup() {
  const dispatch = useAppDispatch();

  const categoryPopupRef = useRef<HTMLDivElement>(null);
  const categoryListRef = useRef<HTMLDivElement>(null);
  const visible = useAppSelector((state) => state.modal.categoryPopupVisible);
  const [categoryData, setCategoryData] = useState<CategoryItem[]>([]);
  const [categoryListHeight, setCategoryListHeight] = useState<string>('');

  const getCrazyDealData = async () => {
    try {
      const res = await systemApi.getCategoryTree();
      if (res.data && res.data.tree && res.data.tree.length) {
        res.data.tree.forEach((v: CategoryItem) => {
          v.showSubCategory = false;

          v.subCategories.forEach((v1: SubCategoryItem) => {
            v1.showSubCategory = false;
          });
        });
        setCategoryData(res.data.tree);
        // sessionStorageService.setItem('categoryTree', JSON.stringify(res.data.tree))
      }
    } catch {
      return { notFound: true };
    }
  };

  useEffect(() => {
    getCrazyDealData();
  }, []);

  // 计算并设置categoryList的高度
  useEffect(() => {
    if (visible) {
      const calculateHeight = () => {
        const contentElement = document.getElementById('categoryPopupContent');
        const titleElement = document.getElementById('categoryPopupTitle');
        
        if (contentElement && titleElement) {
          const contentHeight = contentElement.clientHeight;
          const titleHeight = titleElement.clientHeight;
          const listHeight = contentHeight - titleHeight;
          
          setCategoryListHeight(`${listHeight}px`);
        }
      };
      
      // 初始计算
      setTimeout(calculateHeight, 100);
      
      // 监听窗口大小变化，重新计算高度
      window.addEventListener('resize', calculateHeight);
      
      return () => {
        window.removeEventListener('resize', calculateHeight);
      };
    }
  }, [visible]);
  
  // 当分类数据变化时重新计算高度
  useEffect(() => {
    if (visible && categoryData.length > 0) {
      setTimeout(() => {
        const contentElement = document.getElementById('categoryPopupContent');
        const titleElement = document.getElementById('categoryPopupTitle');
        
        if (contentElement && titleElement) {
          const contentHeight = contentElement.clientHeight;
          const titleHeight = titleElement.clientHeight;
          const listHeight = contentHeight - titleHeight;
          
          setCategoryListHeight(`${listHeight}px`);
        }
      }, 100);
    }
  }, [categoryData, visible]);

  const handleViewDetail = (url: string) => {
    dispatch(closeCategoryPopup());
    window.location.href = url
  };

  const handleClose = () => {
    dispatch(closeCategoryPopup());
  };

  const handleCategoryClick = (
    rootCategory: number,
    subCategory: number | string
  ) => {
    const data = JSON.parse(JSON.stringify(categoryData));
    if (!subCategory) {
      // 如果没有子分类，则直接切换根分类的展示
      data.forEach((item: CategoryItem) => {
        if (item.categoryID == rootCategory) {
          item.showSubCategory = !item.showSubCategory;
        }
      });
    }
    {
      // 如果有子分类，证明根分类无需处理，直接切换子分类的展示
      data.forEach((item: CategoryItem) => {
        if (item.categoryID == rootCategory && item.subCategories && item.subCategories.length > 0) {
          item.subCategories.forEach((v1: SubCategoryItem) => {
            if (v1.categoryID == subCategory) {
              v1.showSubCategory = !v1.showSubCategory;
            }
          });
        }
      });
    }

    setCategoryData(data);
  };

  return (
    <div className={styles["comp-category-popup"]} ref={categoryPopupRef}>
      <Popup
        teleport={() => categoryPopupRef.current || document.body}
        visible={visible}
        onClose={handleClose}
        onClickOverlay={handleClose}
        position="left"
        closeOnPopstate={true}
        closeOnClickOverlay={false}
        safeAreaInsetBottom={true}
        className={styles["category-popup-box"]}
        style={{ width: "100%", height: "100%", background: "#DDDDDD" }}
        overlayStyle={{ background: "rgba(0, 0, 0, 0.83)" }}
      >
        <div className={styles["category-popup-content"]} id="categoryPopupContent">
          {
            visible ? (
              <div className={styles["category-popup-close"]} onClick={handleClose}>
                <Cross color={'#FFFFFF'} fontSize={28} />
              </div>
            ) : null
          }
          <div className={styles["category-popup-title"]} id="categoryPopupTitle">All Categories</div>

          {
            categoryData && categoryData.length > 0 ? (
              <div 
                className={styles["category-list"]} 
                id="categoryList"
                ref={categoryListRef}
                style={{ height: categoryListHeight || 'calc(100vh - 103px)' }}
              >
                {categoryData.map((category: CategoryItem) => (
                  <div className={styles["category-item"]} key={category.categoryID}>
                    <div
                      className={styles["category-item-content"]}
                      onClick={() => handleCategoryClick(category.categoryID, "")}
                    >
                      <Image
                        className={styles["category-icon"]}
                        src={`${process.env.IMAGE_DOMAIN}/svg/category/icon-category-${category.categoryID}.svg`}
                        alt={category.name}
                        width={20}
                        height={20}
                      ></Image>

                      <div className={styles["category-name"]}>{category.name}</div>

                      <Arrow
                        className={styles["arrow-icon"]}
                        rotate={category.showSubCategory ? 90 : 0}
                      />
                    </div>
                    {category.showSubCategory ? (
                      <div className={styles["category-item-sub-content"]}>
                        <div className={styles["all-category-link"]} onClick={() => handleViewDetail(`/${category.url}`)}>
                          All {category.name}
                        </div>
                        {category.subCategories.map(
                          (subCategory: SubCategoryItem, subIndex: number) => (
                            <div
                              className={styles["sub-category-item"]}
                              key={`${subCategory.name}-${subIndex}`}
                            >
                              <div
                                className={styles["sub-category-content"]}
                                onClick={() =>
                                  handleCategoryClick(
                                    category.categoryID,
                                    subCategory.categoryID
                                  )
                                }
                              >
                                <div className={styles["arrow-icon-box"]}>
                                  <Arrow className={styles["arrow-icon"]} rotate={subCategory.showSubCategory ? 90 : 0} />
                                </div>
                                <div className={styles["sub-category-name"]} onClick={() => handleViewDetail(`/${subCategory.url}`)}>{subCategory.name}</div>
                              </div>
                              {subCategory.showSubCategory &&
                              subCategory.subCategories &&
                              subCategory.subCategories.length > 0
                                ? subCategory.subCategories.map(
                                    (grandChild: GrandChildCategoryItem, grandIndex: number) => (
                                      <div
                                        className={styles["grand-child-content"]}
                                        key={`${grandChild.name}-${grandIndex}`}
                                        onClick={() => handleViewDetail(`/${grandChild.url}`)}
                                      >
                                        {grandChild.name}
                                      </div>
                                    )
                                  )
                                : null}
                            </div>
                          )
                        )}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

            ) : <Skeleton rowHeight={14} style={{ paddingTop: '20px' }}></Skeleton>
          }
        </div>
      </Popup>
    </div>
  );
}
